import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, ArrowDown, Target, Sparkles, Users, Check, Send, Loader2, Copy, User, Building, Mail, Phone, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TieTheMissionPage = () => {
  const [viewMode, setViewMode] = useState('content'); // 'content', 'form', 'generating', 'result', 'success', 'message'
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [generatedMission, setGeneratedMission] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    title: '',
    phone: '',
    roles: '',
    worldNeed: '',
    gift: '',
    finalMissionStatement: '',
    honeypot: ''
  });

  // Message form
  const [messageData, setMessageData] = useState({
    message: '',
    canShare: false
  });
  const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);

  // Scroll to top on mount and view changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [viewMode]);

  // Form steps configuration
  const formSteps = [
    {
      id: 'name',
      icon: User,
      title: "Let's start with your name",
      subtitle: "We'll use this to personalize your mission statement.",
      field: 'name',
      type: 'text',
      placeholder: "Enter your full name",
      required: true
    },
    {
      id: 'email',
      icon: Mail,
      title: "What's your email?",
      subtitle: "We'll send you a copy of your mission statement.",
      field: 'email',
      type: 'email',
      placeholder: "you@example.com",
      required: true
    },
    {
      id: 'organization',
      icon: Building,
      title: "What organization are you with?",
      subtitle: "This helps us understand your context.",
      field: 'organization',
      type: 'text',
      placeholder: "Your company or organization",
      required: true
    },
    {
      id: 'title',
      icon: User,
      title: "What's your title or role?",
      subtitle: "Your professional position or role.",
      field: 'title',
      type: 'text',
      placeholder: "CEO, Manager, Director, etc.",
      required: true
    },
    {
      id: 'phone',
      icon: Phone,
      title: "Your phone number",
      subtitle: "Optional, but helpful if Tyrre wants to follow up.",
      field: 'phone',
      type: 'tel',
      placeholder: "(555) 123-4567",
      required: false
    },
    {
      id: 'roles',
      icon: Users,
      title: "Who are you?",
      subtitle: "List the roles you are called to live. Think beyond your job title — who are you at your core?",
      field: 'roles',
      type: 'textarea',
      placeholder: "Example: man, father, husband, Christian, leader, mentor, friend...",
      required: true,
      hint: "These are the identities that define you across all areas of life."
    },
    {
      id: 'worldNeed',
      icon: Target,
      title: "What does the world need?",
      subtitle: "What problem are you meant to respond to? What does the world lack that you feel called to help provide?",
      field: 'worldNeed',
      type: 'textarea',
      placeholder: "Example: love, acceptance, safety, innovation, hope, healing, leadership...",
      required: true,
      hint: "Think about the gap you see in the world that stirs something in you."
    },
    {
      id: 'gift',
      icon: Sparkles,
      title: "What gift have you been given?",
      subtitle: "How are you uniquely equipped to serve? What abilities, experiences, or perspectives do you bring?",
      field: 'gift',
      type: 'textarea',
      placeholder: "Example: embracing adversity with humility, strategic thinking, empathy, teaching, connecting people...",
      required: true,
      hint: "These are the tools you've been given to fulfill your purpose."
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateCurrentStep = () => {
    const step = formSteps[currentStep];
    const value = formData[step.field];
    
    if (step.required && (!value || value.trim() === '')) {
      toast.error(`Please fill in this field`);
      return false;
    }
    
    if (step.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        toast.error('Please enter a valid email address');
        return false;
      }
    }
    
    return true;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;
    
    if (currentStep < formSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // All steps complete, generate mission
      handleGenerateMission();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    } else {
      setViewMode('content');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && formSteps[currentStep].type !== 'textarea') {
      e.preventDefault();
      handleNext();
    }
  };

  const handleGenerateMission = async () => {
    setViewMode('generating');
    
    try {
      const response = await axios.post(`${API}/mission/generate`, {
        roles: formData.roles,
        world_need: formData.worldNeed,
        gifts: formData.gift,
        faith_mode: true
      });
      
      setGeneratedMission(response.data);
      setFormData(prev => ({ ...prev, finalMissionStatement: response.data.standard_mission }));
      setViewMode('result');
    } catch (error) {
      console.error('Error generating mission:', error);
      toast.error('Failed to generate mission. Please try again.');
      setViewMode('form');
      setCurrentStep(formSteps.length - 1);
    }
  };

  const handleSaveMission = async () => {
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/submissions/submit`, {
        name: formData.name,
        email: formData.email,
        organization: formData.organization,
        title: formData.title,
        phone: formData.phone || null,
        roles: formData.roles,
        world_need: formData.worldNeed,
        gift: formData.gift,
        final_mission_statement: formData.finalMissionStatement,
        honeypot: formData.honeypot
      });

      setSubmissionId(response.data.id);
      setViewMode('success');
      toast.success('Your mission has been saved!');
    } catch (error) {
      console.error('Error submitting mission:', error);
      if (error.response?.status === 429) {
        toast.error('Too many submissions. Please try again later.');
      } else {
        toast.error('Failed to save mission. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    
    if (!messageData.message) {
      toast.error('Please enter a message');
      return;
    }

    setIsSubmittingMessage(true);

    try {
      await axios.post(`${API}/submissions/message`, {
        submission_id: submissionId,
        name: formData.name,
        email: formData.email,
        message: messageData.message,
        can_share: messageData.canShare
      });

      setViewMode('message');
      toast.success('Message sent to Tyrre!');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmittingMessage(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleDownloadPDF = async () => {
    try {
      toast.loading('Generating PDF...');
      const response = await axios.post(`${API}/pdf/mission`, {
        name: formData.name,
        short_mission: generatedMission.short_mission,
        standard_mission: generatedMission.standard_mission,
        manifesto_mission: generatedMission.manifesto_mission,
        behavioral_standards: generatedMission.behavioral_standards,
        integration_plan: generatedMission.integration_plan,
        roles: formData.roles,
        world_need: formData.worldNeed,
        gift: formData.gift
      }, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mission-statement-${formData.name.replace(/\s+/g, '-').toLowerCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.dismiss();
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  const startForm = () => {
    setViewMode('form');
    setCurrentStep(0);
  };

  // Typeform-style form view
  if (viewMode === 'form') {
    const step = formSteps[currentStep];
    const StepIcon = step.icon;
    const progress = ((currentStep + 1) / formSteps.length) * 100;

    return (
      <main className="min-h-screen bg-[#0d0d0d] flex flex-col">
        {/* Progress bar */}
        <div className="fixed top-0 left-0 right-0 h-1 bg-[#1a1a18] z-50">
          <div 
            className="h-full bg-[#c9a86c] transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Back button */}
        <div className="fixed top-20 left-4 md:left-8 z-40">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-[#a8a8a0] hover:text-[#c9a86c] transition-colors"
            data-testid="form-back-button"
          >
            <ArrowLeft size={20} />
            <span className="hidden md:inline">Back</span>
          </button>
        </div>

        {/* Step counter */}
        <div className="fixed top-20 right-4 md:right-8 z-40">
          <span className="text-[#6b6b65] text-sm">
            {currentStep + 1} / {formSteps.length}
          </span>
        </div>

        {/* Main content */}
        <div className="flex-1 flex items-center justify-center px-4 py-32">
          <div className="w-full max-w-xl mx-auto">
            {/* Icon */}
            <div className="w-16 h-16 flex items-center justify-center border border-[#c9a86c]/30 text-[#c9a86c] mx-auto mb-8">
              <StepIcon size={32} />
            </div>

            {/* Question */}
            <h1 className="text-2xl md:text-3xl font-display text-[#f5f5f0] text-center mb-3">
              {step.title}
            </h1>
            <p className="text-[#a8a8a0] text-center mb-8">
              {step.subtitle}
            </p>

            {/* Input */}
            <div className="space-y-4">
              {step.type === 'textarea' ? (
                <Textarea
                  value={formData[step.field]}
                  onChange={(e) => handleInputChange(step.field, e.target.value)}
                  placeholder={step.placeholder}
                  rows={4}
                  className="w-full bg-transparent border-0 border-b-2 border-[#c9a86c]/30 focus:border-[#c9a86c] rounded-none text-[#f5f5f0] text-xl placeholder:text-[#6b6b65] resize-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 py-4"
                  autoFocus
                  data-testid={`form-input-${step.field}`}
                />
              ) : (
                <Input
                  type={step.type}
                  value={formData[step.field]}
                  onChange={(e) => handleInputChange(step.field, e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={step.placeholder}
                  className="w-full bg-transparent border-0 border-b-2 border-[#c9a86c]/30 focus:border-[#c9a86c] rounded-none text-[#f5f5f0] text-xl placeholder:text-[#6b6b65] h-16 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0"
                  autoFocus
                  data-testid={`form-input-${step.field}`}
                />
              )}
              
              {step.hint && (
                <p className="text-sm text-[#6b6b65] italic">
                  {step.hint}
                </p>
              )}
            </div>

            {/* Continue button */}
            <div className="mt-10 flex justify-center">
              <Button
                onClick={handleNext}
                className="btn-primary px-8 h-14 text-lg inline-flex items-center gap-2"
                data-testid="form-continue-button"
              >
                {currentStep === formSteps.length - 1 ? (
                  <>Generate My Mission<Sparkles size={20} className="ml-2" /></>
                ) : (
                  <>Continue<ArrowRight size={20} className="ml-2" /></>
                )}
              </Button>
            </div>

            {/* Press Enter hint */}
            {step.type !== 'textarea' && (
              <p className="text-center text-[#6b6b65] text-sm mt-4">
                Press <span className="text-[#c9a86c]">Enter ↵</span> to continue
              </p>
            )}
          </div>
        </div>
      </main>
    );
  }

  // Generating view
  if (viewMode === 'generating') {
    return (
      <main className="min-h-screen bg-[#0d0d0d] flex items-center justify-center">
        <div className="text-center px-4">
          <div className="w-20 h-20 flex items-center justify-center border border-[#c9a86c]/30 text-[#c9a86c] mx-auto mb-8 animate-pulse">
            <Sparkles size={40} />
          </div>
          <h1 className="text-2xl md:text-3xl font-display text-[#f5f5f0] mb-4">
            Revealing Your Mission
          </h1>
          <p className="text-[#a8a8a0] mb-8">
            Your mission has been inside you all along. Let's bring it to light...
          </p>
          <Loader2 size={32} className="text-[#c9a86c] animate-spin mx-auto" />
        </div>
      </main>
    );
  }

  // Result view - show generated mission
  if (viewMode === 'result' && generatedMission) {
    return (
      <main className="min-h-screen bg-[#0d0d0d] pt-32 pb-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="w-20 h-20 flex items-center justify-center bg-[#c9a86c]/10 border border-[#c9a86c] rounded-full mx-auto mb-6">
                <Sparkles size={40} className="text-[#c9a86c]" />
              </div>
              <h1 className="heading-2 text-[#f5f5f0] mb-4">{formData.name}, Your Mission Was Always There</h1>
              <p className="body-large text-[#a8a8a0]">
                We didn't create this—we simply revealed what was already inside you.
              </p>
            </div>

            {/* Mission Statements */}
            <div className="space-y-6 mb-10">
              {/* Short Mission */}
              <div className="bg-[#1a1a18] border border-[#c9a86c]/30 p-6 md:p-8 relative">
                <button
                  onClick={() => copyToClipboard(generatedMission.short_mission)}
                  className="absolute top-4 right-4 text-[#a8a8a0] hover:text-[#c9a86c] transition-colors"
                  data-testid="copy-short-mission"
                >
                  <Copy size={18} />
                </button>
                <span className="caption text-[#c9a86c] mb-3 block">Short Mission (12-20 words)</span>
                <p className="text-xl md:text-2xl font-display italic text-[#f5f5f0]">
                  "{generatedMission.short_mission}"
                </p>
              </div>

              {/* Standard Mission */}
              <div className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-6 md:p-8 relative">
                <button
                  onClick={() => copyToClipboard(generatedMission.standard_mission)}
                  className="absolute top-4 right-4 text-[#a8a8a0] hover:text-[#c9a86c] transition-colors"
                  data-testid="copy-standard-mission"
                >
                  <Copy size={18} />
                </button>
                <span className="caption text-[#c9a86c] mb-3 block">Professional Mission Statement</span>
                <p className="text-lg text-[#f5f5f0]">
                  {generatedMission.standard_mission}
                </p>
              </div>

              {/* Manifesto */}
              <div className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-6 md:p-8 relative">
                <button
                  onClick={() => copyToClipboard(generatedMission.manifesto_mission)}
                  className="absolute top-4 right-4 text-[#a8a8a0] hover:text-[#c9a86c] transition-colors"
                  data-testid="copy-manifesto"
                >
                  <Copy size={18} />
                </button>
                <span className="caption text-[#c9a86c] mb-3 block">Your Manifesto</span>
                <p className="text-lg text-[#f5f5f0] whitespace-pre-line italic">
                  {generatedMission.manifesto_mission}
                </p>
              </div>
            </div>

            {/* Behavioral Standards */}
            <div className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-6 md:p-8 mb-6">
              <span className="caption text-[#c9a86c] mb-4 block">Your Behavioral Standards</span>
              <ul className="space-y-3">
                {generatedMission.behavioral_standards.map((standard, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check size={18} className="text-[#c9a86c] mt-1 flex-shrink-0" />
                    <span className="text-[#f5f5f0]">{standard}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* 7-Day Integration Plan */}
            <div className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-6 md:p-8 mb-10">
              <span className="caption text-[#c9a86c] mb-4 block">7-Day Integration Plan</span>
              <div className="space-y-4">
                {generatedMission.integration_plan.map((day) => (
                  <div key={day.day} className="flex gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#c9a86c]/10 border border-[#c9a86c]/30 text-[#c9a86c] font-bold flex-shrink-0">
                      {day.day}
                    </div>
                    <p className="text-[#a8a8a0] pt-2">{day.action}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Edit Final Statement */}
            <div className="bg-[#c9a86c]/10 border border-[#c9a86c]/30 p-6 md:p-8 mb-8">
              <Label className="text-[#f5f5f0] mb-2 block">Your Final Mission Statement</Label>
              <p className="text-sm text-[#a8a8a0] mb-4">
                Feel free to edit and make it your own before saving.
              </p>
              <Textarea
                value={formData.finalMissionStatement}
                onChange={(e) => handleInputChange('finalMissionStatement', e.target.value)}
                rows={3}
                className="bg-[#0d0d0d] border-[rgba(168,168,160,0.2)] text-[#f5f5f0] focus:border-[#c9a86c] text-lg"
                data-testid="final-mission-textarea"
              />
            </div>

            {/* Save Button */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => {
                  setViewMode('form');
                  setCurrentStep(formSteps.length - 1);
                }}
                className="btn-secondary"
                data-testid="back-to-edit-button"
              >
                <ArrowLeft size={18} className="mr-2" />
                Edit My Answers
              </Button>
              <Button
                onClick={handleDownloadPDF}
                className="btn-secondary"
                data-testid="download-pdf-button"
              >
                <Download size={18} className="mr-2" />
                Download PDF
              </Button>
              <Button
                onClick={handleSaveMission}
                disabled={isSubmitting}
                className="btn-primary px-8 h-14 text-lg"
                data-testid="save-mission-button"
              >
                {isSubmitting ? (
                  <><Loader2 size={20} className="mr-2 animate-spin" />Saving...</>
                ) : (
                  <>Save My Mission<Check size={20} className="ml-2" /></>
                )}
              </Button>
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Success view
  if (viewMode === 'success' || viewMode === 'message') {
    return (
      <main className="min-h-screen bg-[#0d0d0d] pt-32 pb-20">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <div className="w-20 h-20 flex items-center justify-center bg-[#c9a86c]/10 border border-[#c9a86c] rounded-full mx-auto mb-6">
                <Check size={40} className="text-[#c9a86c]" />
              </div>
              <h1 className="heading-2 text-[#f5f5f0] mb-4">Your Mission is Saved</h1>
              <p className="body-large text-[#a8a8a0] mb-8">
                You've taken the first step toward living tied to purpose.
              </p>
              
              <div className="bg-[#1a1a18] border border-[#c9a86c]/30 p-8 mb-8 text-left relative">
                <button
                  onClick={() => copyToClipboard(formData.finalMissionStatement)}
                  className="absolute top-4 right-4 text-[#a8a8a0] hover:text-[#c9a86c] transition-colors"
                >
                  <Copy size={18} />
                </button>
                <span className="caption text-[#c9a86c] mb-4 block">Your Mission Statement</span>
                <p className="text-xl font-display italic text-[#f5f5f0]">
                  "{formData.finalMissionStatement}"
                </p>
              </div>
              
              {/* Download PDF Button */}
              {generatedMission && (
                <div className="text-center mb-8">
                  <Button
                    onClick={handleDownloadPDF}
                    className="btn-secondary inline-flex items-center gap-2"
                    data-testid="download-pdf-success"
                  >
                    <Download size={18} />
                    Download PDF
                  </Button>
                </div>
              )}
            </div>

            {viewMode === 'success' && (
              <div className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-8">
                <h3 className="heading-3 text-[#f5f5f0] mb-4">Share Your Experience</h3>
                <p className="body-medium text-[#a8a8a0] mb-6">
                  How has building your mission impacted you? Send Tyrre a message.
                </p>
                
                <form onSubmit={handleMessageSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#f5f5f0]">Your Message</Label>
                    <Textarea
                      id="message"
                      value={messageData.message}
                      onChange={(e) => setMessageData(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Share how this experience has impacted you..."
                      rows={4}
                      className="bg-[#0d0d0d] border-[rgba(168,168,160,0.2)] text-[#f5f5f0] placeholder:text-[#6b6b65] focus:border-[#c9a86c]"
                      data-testid="message-textarea"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="canShare"
                      checked={messageData.canShare}
                      onCheckedChange={(checked) => setMessageData(prev => ({ ...prev, canShare: checked }))}
                      className="border-[rgba(168,168,160,0.3)] data-[state=checked]:bg-[#c9a86c] data-[state=checked]:border-[#c9a86c]"
                    />
                    <Label htmlFor="canShare" className="text-sm text-[#a8a8a0]">
                      I'm okay with you sharing my message (first name only) on the site
                    </Label>
                  </div>

                  <Button type="submit" disabled={isSubmittingMessage} className="btn-primary w-full" data-testid="send-message-button">
                    {isSubmittingMessage ? (
                      <><Loader2 size={18} className="mr-2 animate-spin" />Sending...</>
                    ) : (
                      <>Send Message<Send size={18} className="ml-2" /></>
                    )}
                  </Button>
                </form>
              </div>
            )}

            {viewMode === 'message' && (
              <div className="text-center">
                <p className="body-large text-[#c9a86c] mb-8">
                  Thank you for sharing! Tyrre will read your message.
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
              <Link to="/" className="btn-secondary inline-flex items-center justify-center gap-2">
                Return Home
              </Link>
              <Link to="/book" className="btn-primary inline-flex items-center justify-center gap-2">
                Book Tyrre to Speak
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Main content view (landing)
  return (
    <main className="bg-[#0d0d0d]">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-[#0d0d0d] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[#c9a86c]/5 rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-[#c9a86c]/10 rounded-full"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="caption text-[#c9a86c] mb-4 block">The Framework</span>
            <h1 className="heading-1 text-[#f5f5f0] mb-6">A Life Tied to Purpose</h1>
            <div className="gold-accent mx-auto mb-6"></div>
            <p className="body-large text-[#a8a8a0] mb-8">
              The Ty the Mission Framework brings your life into alignment by answering three essential questions.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button onClick={startForm} className="btn-primary inline-flex items-center justify-center gap-2 h-14 px-8" data-testid="start-mission-button">
                Discover Your Mission
                <ArrowRight size={18} />
              </Button>
              <Link to="/book" className="btn-secondary inline-flex items-center justify-center gap-2 h-14 px-8">
                Book Tyrre to Speak
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Three Questions */}
      <section className="py-16 bg-[#1a1a18]">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#0d0d0d] border border-[rgba(168,168,160,0.1)] p-8 text-center">
              <div className="w-14 h-14 flex items-center justify-center border border-[#c9a86c]/30 text-[#c9a86c] mx-auto mb-6">
                <Users size={28} />
              </div>
              <h3 className="heading-3 text-[#f5f5f0] mb-3">Who are you?</h3>
              <p className="text-[#a8a8a0]">The roles you are called to live.</p>
            </div>
            <div className="bg-[#0d0d0d] border border-[rgba(168,168,160,0.1)] p-8 text-center">
              <div className="w-14 h-14 flex items-center justify-center border border-[#c9a86c]/30 text-[#c9a86c] mx-auto mb-6">
                <Target size={28} />
              </div>
              <h3 className="heading-3 text-[#f5f5f0] mb-3">What does the world need?</h3>
              <p className="text-[#a8a8a0]">The problem you are meant to respond to.</p>
            </div>
            <div className="bg-[#0d0d0d] border border-[rgba(168,168,160,0.1)] p-8 text-center">
              <div className="w-14 h-14 flex items-center justify-center border border-[#c9a86c]/30 text-[#c9a86c] mx-auto mb-6">
                <Sparkles size={28} />
              </div>
              <h3 className="heading-3 text-[#f5f5f0] mb-3">What gift have you been given?</h3>
              <p className="text-[#a8a8a0]">The way you are uniquely equipped to serve.</p>
            </div>
          </div>
          
          <div className="max-w-3xl mx-auto text-center mt-12">
            <p className="body-large text-[#f5f5f0]">
              When these questions are answered honestly and integrated fully, they produce more than clarity—<span className="text-[#c9a86c]">they produce direction.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Tyrre's Mission Example */}
      <section className="section-padding bg-[#0d0d0d]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="caption text-[#c9a86c] mb-4 block">In Practice</span>
              <h2 className="heading-2 text-[#f5f5f0] mb-6">My Mission</h2>
              <div className="gold-accent mx-auto"></div>
            </div>

            <div className="bg-[#1a1a18] border border-[#c9a86c]/30 p-8 md:p-10 mb-10">
              <p className="text-xl md:text-2xl font-display italic text-[#f5f5f0] text-center leading-relaxed">
                "As a man, father, husband, and Christian leader, I co-create a world of love, acceptance, and safety by embracing adversity and humility, seeking knowledge, and responding with wisdom."
              </p>
            </div>

            <div className="space-y-6 mb-10">
              <p className="body-large text-[#c9a86c] font-medium">
                This statement is not aspirational—it is operational.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#c9a86c] rounded-full mt-3 flex-shrink-0"></span>
                  <span className="body-large text-[#a8a8a0]">It unifies every role I hold.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#c9a86c] rounded-full mt-3 flex-shrink-0"></span>
                  <span className="body-large text-[#a8a8a0]">It filters every decision I make.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-[#c9a86c] rounded-full mt-3 flex-shrink-0"></span>
                  <span className="body-large text-[#a8a8a0]">It guides how I respond under pressure and how I show up in moments of opportunity.</span>
                </li>
              </ul>
            </div>

            <div className="text-center">
              <Button onClick={startForm} className="btn-primary inline-flex items-center gap-2" data-testid="start-mission-button-2">
                Discover Your Mission
                <ArrowRight size={18} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* The Result */}
      <section className="section-padding bg-[#1a1a18]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="caption text-[#c9a86c] mb-4 block">The Result</span>
              <h2 className="heading-2 text-[#f5f5f0] mb-6">When Your Mission is Clear</h2>
              <div className="gold-accent mx-auto"></div>
            </div>

            <div className="space-y-6 mb-10">
              <p className="body-large text-[#f5f5f0]">
                Once your mission is this clear, <span className="text-[#c9a86c]">life stops competing with itself.</span>
              </p>
              <p className="body-large text-[#a8a8a0]">
                You no longer separate who you are at home from who you are in leadership, or who you are in faith from how you navigate adversity. Your calendar begins to reflect your convictions. Your execution increases because your purpose is settled.
              </p>
              <div className="border-l-2 border-[#c9a86c] pl-6 py-2">
                <p className="text-xl font-display italic text-[#f5f5f0]">
                  This is what it means to live tied to purpose.
                </p>
              </div>
            </div>

            <div className="space-y-4 mb-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
                <p className="body-large text-[#6b6b65]">Not a life without challenge—</p>
                <p className="body-large text-[#f5f5f0]">but a life <span className="text-[#c9a86c]">anchored in meaning.</span></p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
                <p className="body-large text-[#6b6b65]">Not a life without uncertainty—</p>
                <p className="body-large text-[#f5f5f0]">but a life <span className="text-[#c9a86c]">guided by wisdom.</span></p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center">
                <p className="body-large text-[#6b6b65]">Not a life chasing success—</p>
                <p className="body-large text-[#f5f5f0]">but one <span className="text-[#c9a86c]">compounding impact.</span></p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-xl font-display text-[#f5f5f0] leading-relaxed mb-8">
                When your mission is clear, your life stops drifting—<br />
                <span className="text-[#c9a86c]">and starts building the world you were meant to help create.</span>
              </p>
              <Button onClick={startForm} className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4" data-testid="start-mission-button-3">
                Discover Your Mission Now
                <ArrowRight size={20} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Book Tyrre CTA */}
      <section className="py-20 bg-[#0d0d0d] border-t border-[rgba(168,168,160,0.1)]">
        <div className="container-custom text-center">
          <span className="caption text-[#c9a86c] mb-4 block">Want to Go Deeper?</span>
          <h2 className="heading-2 text-[#f5f5f0] mb-6 max-w-2xl mx-auto">
            Bring Ty the Mission to Your Organization
          </h2>
          <p className="body-large text-[#a8a8a0] mb-10 max-w-xl mx-auto">
            Book Tyrre for a keynote or workshop and give your team the framework for alignment.
          </p>
          <Link to="/book" className="btn-secondary inline-flex items-center gap-2">
            Book Tyrre to Speak
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default TieTheMissionPage;
